/**
 * Async task completion toast notifications.
 *
 * Wraps react-hot-toast with AxiomGO-specific logic:
 * - Visibility guard: skips toast when user is already viewing the result
 * - Click-to-scroll: clicking a toast scrolls to the relevant message bubble
 * - i18n: all strings via i18next (never hardcoded)
 * - Integrates with ConnectionStore's onRawMessage pipeline via handleAsyncTaskEvent
 *
 * Click-to-scroll uses `data-message-id` attributes on message bubble root elements.
 */
import toast from 'react-hot-toast'
import i18n from './i18n/config'
import { useChatStore } from '../stores/chatStore'

/** WS event types that produce a completion toast */
const ASYNC_TASK_EVENTS = [
  'import_complete',
  'import_job_completed',
  'import_error',
  'web_search_completed',
] as const

type AsyncTaskEventType = (typeof ASYNC_TASK_EVENTS)[number]

interface ToastPayload {
  messageId?: string
  conversationId?: string
  /** Number of rows imported — displayed in the import complete message */
  rowCount?: number
}

/**
 * Scroll the DOM to a message bubble by its messageId.
 * Requires the bubble's root element to carry `data-message-id="<id>"`.
 */
function scrollToMessage(messageId: string): void {
  const el = document.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(messageId)}"]`)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.focus({ preventScroll: true })
  }
}

/**
 * Returns true when the toast should be suppressed because the user is
 * already viewing the conversation that produced the event.
 *
 * Guard logic:
 *  - If the document is hidden (background tab) → never skip (user needs the notification)
 *  - If no conversationId is known → cannot determine, skip to be conservative
 *  - If the active conversation matches → skip (result is already on screen)
 */
function shouldSkipToast(conversationId: string | undefined): boolean {
  if (document.visibilityState === 'hidden') return false
  if (!conversationId) return false
  const activeId = useChatStore.getState().activeConversationId
  return activeId === conversationId
}

/** Build a click handler that dismisses the toast and scrolls to the message. */
function makeClickHandler(toastId: string, messageId: string | undefined): () => void {
  return () => {
    toast.dismiss(toastId)
    if (messageId) scrollToMessage(messageId)
  }
}

/**
 * Fire a success toast for a completed import job.
 * Includes row count when available.
 */
export function notifyImportComplete(payload: ToastPayload): void {
  const { messageId, conversationId, rowCount } = payload
  if (shouldSkipToast(conversationId)) return

  const message =
    rowCount != null
      ? i18n.t('toasts.importComplete', { count: rowCount })
      : i18n.t('toasts.importCompleteGeneric')

  const id = `import-${messageId ?? Date.now()}`
  const onClick = makeClickHandler(id, messageId)

  // Sticky: imports take 30-60 s and operators on shop-floor tablets often
  // step away. Auto-dismiss made the operator miss the result and re-upload
  // (card #152 / Onda 2 P2 #11). Operator dismisses by clicking the toast,
  // which also scrolls to the message bubble (existing click handler).
  toast.success(message, {
    id,
    duration: Infinity,
    ariaProps: { role: 'status', 'aria-live': 'polite' },
    style: { cursor: messageId ? 'pointer' : 'default' },
  })

  registerClickHandler(id, onClick)
}

/**
 * Fire a success toast for a completed web search.
 */
export function notifyWebSearchComplete(payload: ToastPayload): void {
  const { messageId, conversationId } = payload
  if (shouldSkipToast(conversationId)) return

  const id = `websearch-${messageId ?? Date.now()}`
  const onClick = makeClickHandler(id, messageId)

  toast.success(i18n.t('toasts.webSearchComplete'), {
    id,
    duration: 6000,
    ariaProps: { role: 'status', 'aria-live': 'polite' },
    style: { cursor: messageId ? 'pointer' : 'default' },
  })

  registerClickHandler(id, onClick)
}

/**
 * Fire an error toast for a failed import job.
 * Includes a retry hint from i18n.
 */
export function notifyImportError(payload: Omit<ToastPayload, 'rowCount'>): void {
  const { conversationId } = payload
  if (shouldSkipToast(conversationId)) return

  // Sticky: same rationale as notifyImportComplete (card #152). An import
  // error is at least as important as a success — the operator must not
  // re-upload thinking nothing happened.
  toast.error(i18n.t('toasts.importError'), {
    duration: Infinity,
    ariaProps: { role: 'alert', 'aria-live': 'assertive' },
  })
}

/**
 * Fire a sticky error toast when the deterministic import EXECUTE call fails
 * (REST POST /api/v1/import/execute — network error, 4xx/5xx). This is the
 * SYNCHRONOUS REST catch path, not the WS `import_error` event handled by
 * `notifyImportError` above.
 *
 * Deliberately does NOT reuse `notifyImportError`: that helper applies
 * `shouldSkipToast` keyed on `conversationId` (suppressing the toast when the
 * user is already viewing the conversation that produced an async WS
 * result). An execute() failure here is local and immediate — the operator
 * just clicked "Importar" and is looking at the screen right now — so the
 * toast must ALWAYS fire (P0-7 Parte A, "falha de importação nunca some em
 * silêncio").
 */
export function notifyImportExecuteError(): void {
  toast.error(i18n.t('import.executeFailed'), {
    duration: Infinity,
    ariaProps: { role: 'alert', 'aria-live': 'assertive' },
  })
}

/**
 * Fire a sticky error toast when the deterministic import ANALYZE call fails
 * (REST POST /api/v1/import/analyze). Always fires — see
 * `notifyImportExecuteError` above for the rationale (local/immediate error,
 * never gated by `shouldSkipToast`).
 */
export function notifyImportAnalyzeError(): void {
  toast.error(i18n.t('import.analyzeFailed'), {
    duration: Infinity,
    ariaProps: { role: 'alert', 'aria-live': 'assertive' },
  })
}

/**
 * Fire a sticky error toast when microphone permission is lost or denied
 * (denied at probe time, or revoked mid-session). Mirrors notifyImportError:
 * i18n string, assertive aria, duration Infinity. Shop-floor criticality — an
 * operator on a tablet may step away, so the toast must not auto-dismiss
 * (card #152 rationale). AppToaster owns the single <Toaster>; do not add another.
 */
export function notifyVoicePermissionLost(): void {
  toast.error(i18n.t('toasts.voicePermissionLost'), {
    duration: Infinity,
    ariaProps: { role: 'alert', 'aria-live': 'assertive' },
  })
}

/**
 * Fire an error toast when the client-side send rate limit (30 msg/min) is hit.
 * P1 Impeccable finding 2: before this, exceeding the cap made the Send button
 * do nothing — the field tech saw a frozen composer with no explanation and
 * assumed the app broke. Tells the user exactly how many seconds to wait.
 */
export function notifyRateLimited(seconds: number): void {
  toast.error(i18n.t('toasts.rateLimited', { seconds }), {
    // Short-lived: the wait is short and the user is looking at the screen
    // right now (they just tapped Send).
    duration: 4000,
    ariaProps: { role: 'alert', 'aria-live': 'assertive' },
  })
}

/**
 * Fire a warning toast when the user tries to send a new message while the
 * PREVIOUS turn on this conversation is still in flight (isTyping === true).
 *
 * RAIZ (achado ao vivo do Denis, 2026-08-25): nada bloqueava um segundo envio
 * enquanto o primeiro turno de uma conversa nova ainda estava em andamento —
 * nem o Enter (prompt-input.tsx chama `onSubmit()` incondicionalmente), nem
 * `sendContent`. Digitar e confirmar antes da saudação terminar de carregar
 * disparava um SEGUNDO turno concorrente para a MESMA conversa; os dois liam
 * o histórico ainda vazio de mensagem do assistente e os dois recebiam a
 * instrução de saudação (`resolveConversationContinuity` no motor), sob a
 * qual o Axis se apresentava duas vezes seguidas — e o segundo turno, sem
 * pista alguma de skill carregada, chutava "equipamento" para um código de
 * PLANO (PLA-00001).
 *
 * O texto do usuário NUNCA é descartado: o chamador (`InputBar.sendContent`)
 * devolve `false` sem limpar o campo, então a mensagem continua ali, pronta
 * para reenviar assim que a resposta em andamento terminar.
 */
export function notifyTurnInFlight(): void {
  toast.error(i18n.t('toasts.turnInFlight'), {
    duration: 4000,
    ariaProps: { role: 'alert', 'aria-live': 'assertive' },
  })
}

/**
 * Fire an error toast when the FRONTEND fails to parse or process an engine
 * message (WS frame unparseable, or the onRawMessage handler throws).
 * P1 Impeccable finding 3: both failure paths only console-logged, so the chat
 * silently stopped updating with no signal. This is DISTINCT from "engine down"
 * (the ConnectionStatus indicator owns that): here the connection is fine but
 * the client dropped a piece of data. Fixed id so a burst of dropped frames
 * collapses into a single toast instead of stacking.
 */
export function notifyEngineMessageDropped(): void {
  toast.error(i18n.t('toasts.messageDropped'), {
    id: 'engine-message-dropped',
    duration: Infinity,
    ariaProps: { role: 'alert', 'aria-live': 'assertive' },
  })
}

/** Stable id: a burst of curator saves collapses into ONE notice instead of stacking. */
export const MEMORY_UPDATED_TOAST_ID = 'memory-fact-saved'
/** How long the "Memory updated" notice stays on screen, at most. */
export const MEMORY_UPDATED_TOAST_MS = 3000
let memoryUpdatedDeadline: ReturnType<typeof setTimeout> | null = null

/**
 * "Memória atualizada" — the curator saved facts after a turn.
 *
 * QA 02/11/2026 (develop): the notice stayed on screen for more than a minute.
 * react-hot-toast pauses EVERY toast's timer while the pointer is over any
 * toast (`startPause` on the container's mouseenter); a mouseleave that never
 * fires — pointer resting on the toast, a tap on a touch screen, the hovered
 * toast removed from under the cursor — leaves the pause on for good and the
 * 3 s notice never expires. This notice carries no action, so it owns its own
 * deadline: `toast.dismiss` at the end, independent of the library's pause.
 */
export function notifyMemoryUpdated(): void {
  toast.success(i18n.t('memory.updated'), {
    id: MEMORY_UPDATED_TOAST_ID,
    duration: MEMORY_UPDATED_TOAST_MS,
  })
  if (memoryUpdatedDeadline !== null) clearTimeout(memoryUpdatedDeadline)
  memoryUpdatedDeadline = setTimeout(() => {
    memoryUpdatedDeadline = null
    toast.dismiss(MEMORY_UPDATED_TOAST_ID)
  }, MEMORY_UPDATED_TOAST_MS)
}

// ─── Click handler registry ───────────────────────────────────────────────────
// react-hot-toast does not expose onClick in ToastOptions. We store handlers
// keyed by toast id; AppToaster reads this map to wire up clicks via the
// Toaster children render prop.

const clickHandlers = new Map<string, () => void>()

/** Register a click handler for a given toast id. */
export function registerClickHandler(id: string, handler: () => void): void {
  clickHandlers.set(id, handler)
}

/** Retrieve and remove the click handler for a toast id. */
export function consumeClickHandler(id: string): (() => void) | undefined {
  const h = clickHandlers.get(id)
  clickHandlers.delete(id)
  return h
}

/** Get click handler without consuming (for repeated clicks before dismiss). */
export function getClickHandler(id: string): (() => void) | undefined {
  return clickHandlers.get(id)
}

// ─── WS event dispatcher ─────────────────────────────────────────────────────

/**
 * Route a raw WS message to the appropriate notification function.
 * Call this from ConnectionStore's onRawMessage handler.
 */
export function handleAsyncTaskEvent(type: string, msg: Record<string, unknown>): void {
  if (!ASYNC_TASK_EVENTS.includes(type as AsyncTaskEventType)) return

  const messageId = typeof msg['messageId'] === 'string' ? msg['messageId'] : undefined
  const conversationId =
    typeof msg['conversationId'] === 'string' ? msg['conversationId'] : undefined

  if (type === 'import_complete' || type === 'import_job_completed') {
    const rowCount =
      typeof msg['totalImported'] === 'number'
        ? msg['totalImported']
        : typeof msg['rowCount'] === 'number'
          ? msg['rowCount']
          : undefined
    notifyImportComplete({ messageId, conversationId, rowCount })
    return
  }

  if (type === 'import_error') {
    notifyImportError({ messageId, conversationId })
    return
  }

  if (type === 'web_search_completed') {
    notifyWebSearchComplete({ messageId, conversationId })
  }
}
